// ARV (reno) / ACV (build) panel — base value, two-way PPSF binding,
// the price-per-sqft slider, and the snap-to-target buttons. Mirrors
// src/components/ARVPanel.jsx in the React standalone, including the
// reno-only ARV composition breakdown, 70% Rule check, and the build-
// only Soundness Check (profit margin / LTC / LTV).
//
// PPSF is a UI-local value derived from baseARV / originalSqft. Editing
// PPSF writes baseARV; "Snap to 20% ROI" / "Snap to Ideal Max" walk
// back through the targetARV20 / targetARVForIMO solver outputs, then
// strip enclosedAddedValue and the marketing multiplier to land the
// underlying baseARV the rest of the math chain consumes.
//
// The panel scaffolds its full DOM once during construction and stores
// refs to every input, slider, readout, and label as instance fields.
// render() then just updates those values + toggles visibility — the
// PPSF slider is never destroyed, so its drag tracking survives the
// re-render loop and linked math (baseARV, derived PPSF readouts,
// composition rows, ARV total in deal summary) updates live during a
// drag, the same way the Acquisition reduction slider already does.

import {
	computeDealMetrics,
	gradeLTC,
	gradeLTV,
	gradeProfitMargin,
} from '../calculations';
import {IDealState} from '../defaults';
import {currencyInput, panelSection} from '../primitives';

export interface IARVPanelHost {
	getState(): IDealState;
	update(patch: Partial<IDealState>): void;
}

export class ARVPanel {
	private rootEl: HTMLElement | null;
	private titleEl: HTMLElement | null = null;
	private subtitleEl: HTMLElement | null = null;
	private bodyEl: HTMLElement | null = null;
	private pricePerSqft: number = 0;

	// Control row
	private controlRowEl: HTMLElement | null = null;
	private baseLabelEl: HTMLElement | null = null;
	private baseInput: HTMLInputElement | null = null;
	private ppsfLabelText: HTMLElement | null = null;
	private ppsfLinkedBadge: HTMLElement | null = null;
	private ppsfInput: HTMLInputElement | null = null;
	private linkBtn: HTMLButtonElement | null = null;
	private linkIcon: HTMLElement | null = null;
	private snap20Btn: HTMLButtonElement | null = null;
	private snapIMBtn: HTMLButtonElement | null = null;

	// Sqft hint (shown when sqft = 0)
	private sqftHintEl: HTMLElement | null = null;

	// Slider block
	private sliderBlockEl: HTMLElement | null = null;
	private sliderSubtitleEl: HTMLElement | null = null;
	private sliderReadoutEl: HTMLElement | null = null;
	private sliderEl: HTMLInputElement | null = null;
	private sliderTickEls: Array<HTMLElement> = [];
	private sliderWarnEl: HTMLElement | null = null;

	// Composition block (reno only, conditional)
	private compositionBlockEl: HTMLElement | null = null;
	private compositionRowsEl: HTMLElement | null = null;
	private compositionTotalEl: HTMLElement | null = null;

	// 70% rule block (reno only)
	private seventyRuleBlockEl: HTMLElement | null = null;
	private seventyRuleStatusEl: HTMLElement | null = null;
	private seventyRuleDetailEl: HTMLElement | null = null;

	// Soundness block (build only)
	private soundnessBlockEl: HTMLElement | null = null;
	private soundnessListEl: HTMLElement | null = null;
	private soundnessNoteEl: HTMLElement | null = null;

	constructor(private host: IARVPanelHost) {
		this.rootEl = document.getElementById('id_lb-deal-panel-arv');
		if (!this.rootEl) {
			return;
		}
		this.scaffoldShell();
		this.scaffoldBody();
		this.recomputePPSFFromState();
		this.render();
	}

	render(): void {
		if (!this.bodyEl) {
			return;
		}
		const state = this.host.getState();
		const m = computeDealMetrics(state);
		const mode = state.mode || 'reno';
		const originalSqft = Number(state.property.sqft) || 0;

		// Two-way sync: external baseARV change refreshes PPSF.
		if (originalSqft > 0) {
			const derived = Math.round((state.baseARV || 0) / originalSqft);
			if (Math.abs(derived - this.pricePerSqft) > 0.5) {
				this.pricePerSqft = derived;
			}
		}

		if (this.titleEl) {
			this.titleEl.textContent = mode === 'build' ? 'As Completed Value (ACV)' : 'Post-Renovation Value (ARV)';
		}
		if (this.subtitleEl) {
			this.subtitleEl.textContent = mode === 'build'
				? 'Comp finished builds within 0.5–1 mile, similar sqft and finishes'
				: 'Pull comps from active/sold listings within 0.5–1 mile, similar sqft';
		}

		// Control row
		if (this.baseLabelEl) {
			this.baseLabelEl.textContent = mode === 'build' ? 'Base ACV (from comps)' : 'Base ARV (from comps)';
		}
		this.setInputValue(this.baseInput, state.baseARV || 0);
		if (this.ppsfLabelText) {
			this.ppsfLabelText.textContent = `Price / sqft${originalSqft > 0 ? ` · ${originalSqft.toLocaleString()} sqft` : ''}`;
		}
		const showPpsfLinked = mode !== 'build' && !!state.valueSyncedToPPSF && (state.enclosedSqft || 0) > 0;
		if (this.ppsfLinkedBadge) {
			this.ppsfLinkedBadge.style.display = showPpsfLinked ? '' : 'none';
		}
		this.setInputValue(this.ppsfInput, this.pricePerSqft);
		if (this.ppsfInput) {
			this.ppsfInput.disabled = originalSqft === 0;
		}
		if (this.linkBtn && this.linkIcon) {
			this.linkBtn.style.display = mode !== 'build' ? '' : 'none';
			this.linkBtn.classList.toggle('lb-deal-arv__link-btn--active', !!state.valueSyncedToPPSF);
			this.linkBtn.title = state.valueSyncedToPPSF
				? 'Linked to leverage panel Value/sqft. Click to unlink.'
				: 'Unlinked. Click to relink Value/sqft to Price/sqft.';
			this.linkBtn.setAttribute('aria-label', state.valueSyncedToPPSF ? 'Unlink' : 'Link');
			this.linkIcon.textContent = state.valueSyncedToPPSF ? 'link' : 'link_off';
		}
		if (this.snap20Btn) {
			this.snap20Btn.disabled = m.totalCashInDeal <= 0;
			const targetForUI = Math.max(0, m.targetARV20 - m.enclosedAddedValue);
			this.snap20Btn.title = `Sets Base ${mode === 'build' ? 'ACV' : 'ARV'} to ${formatCurrency(targetForUI)} — value needed for 20% ROI.`;
		}
		if (this.snapIMBtn) {
			this.snapIMBtn.style.display = mode !== 'build' ? '' : 'none';
			this.snapIMBtn.disabled = m.purchasePrice <= 0;
			this.snapIMBtn.title = m.purchasePrice > 0
				? 'Sets Base ARV so the 70% rule passes — minimum ARV the deal needs given your offer.'
				: 'Enter a purchase price first.';
		}

		// Sqft hint
		if (this.sqftHintEl) {
			this.sqftHintEl.style.display = originalSqft === 0 ? '' : 'none';
		}

		// Slider block
		this.renderSliderBlock(state, m, mode, originalSqft);

		// Composition block (reno + conditional)
		const showComposition = mode !== 'build' && (m.enclosedAddedValue > 0 || (state.marketingMultiplier || 0) > 0);
		if (this.compositionBlockEl) {
			this.compositionBlockEl.style.display = showComposition ? '' : 'none';
			if (showComposition) {
				this.renderCompositionRows(state, m);
			}
		}

		// 70% rule (reno + conditional)
		const showSeventy = mode !== 'build' && m.adjustedARV > 0 && m.totalRenoCost > 0;
		if (this.seventyRuleBlockEl) {
			this.seventyRuleBlockEl.style.display = showSeventy ? '' : 'none';
			if (showSeventy) {
				this.seventyRuleBlockEl.classList.toggle('lb-deal-rule70--pass', !!m.passes70Rule);
				this.seventyRuleBlockEl.classList.toggle('lb-deal-rule70--fail', !m.passes70Rule);
				if (this.seventyRuleStatusEl) {
					this.seventyRuleStatusEl.textContent = m.passes70Rule ? '✓ Passes' : '✗ Above IMO';
				}
				if (this.seventyRuleDetailEl) {
					const listPart = (state.listPrice || 0) > 0 ? `List: ${formatCurrency(state.listPrice || 0)} · ` : '';
					this.seventyRuleDetailEl.textContent =
						`${listPart}Ideal Max Offer (IMO): ${formatCurrency(m.maxAllowableOffer)} · Your offer: ${formatCurrency(m.purchasePrice)}`;
				}
			}
		}

		// Soundness block (build + conditional)
		const showSoundness = mode === 'build' && m.adjustedARV > 0 && m.totalCashInDeal > 0;
		if (this.soundnessBlockEl) {
			this.soundnessBlockEl.style.display = showSoundness ? '' : 'none';
			if (showSoundness) {
				this.renderSoundness(m);
			}
		}
	}

	private renderSliderBlock(state: IDealState, m: ReturnType<typeof computeDealMetrics>, mode: string, originalSqft: number): void {
		if (!this.sliderBlockEl) {
			return;
		}
		const show = originalSqft > 0 && this.pricePerSqft > 0;
		this.sliderBlockEl.style.display = show ? '' : 'none';
		if (!show) {
			return;
		}
		const listingPPSF = m.effectiveListPrice > 0 && originalSqft > 0
			? Math.round(m.effectiveListPrice / originalSqft)
			: 0;
		const costBasisPPSF = originalSqft > 0
			? Math.round((m.purchasePrice + m.totalRenoCost) / originalSqft)
			: 0;
		const anchorPPSF = mode === 'build' ? costBasisPPSF : listingPPSF;
		const anchorLabel = mode === 'build' ? 'cost basis' : 'listing';
		const sliderMin = anchorPPSF > 0 ? anchorPPSF : 50;
		const sliderMax = anchorPPSF > 0 ? Math.round(anchorPPSF * 2.5) : 1000;
		const clampedValue = Math.min(Math.max(this.pricePerSqft, sliderMin), sliderMax);

		if (this.sliderSubtitleEl) {
			const linkedExtra = mode !== 'build' && state.valueSyncedToPPSF && (state.enclosedSqft || 0) > 0
				? ' Leverage panel value/sqft updates in lockstep.'
				: '';
			this.sliderSubtitleEl.textContent =
				`Range: ${anchorLabel} ($${sliderMin.toLocaleString()}/sqft) to +150% ($${sliderMax.toLocaleString()}/sqft).${linkedExtra}`;
		}
		if (this.sliderReadoutEl) {
			this.sliderReadoutEl.textContent = `$${this.pricePerSqft.toLocaleString()}/sqft`;
		}
		if (this.sliderEl) {
			this.sliderEl.min = String(sliderMin);
			this.sliderEl.max = String(sliderMax);
			if (document.activeElement !== this.sliderEl) {
				this.sliderEl.value = String(clampedValue);
			}
		}
		const tickTexts = [
			`$${sliderMin.toLocaleString()} (${anchorLabel})`,
			`$${Math.round((sliderMin + sliderMax) / 2).toLocaleString()}`,
			`$${sliderMax.toLocaleString()} (+150%)`,
		];
		this.sliderTickEls.forEach((el, i) => {
			el.textContent = tickTexts[i];
		});
		if (this.sliderWarnEl) {
			if (this.pricePerSqft < sliderMin) {
				this.sliderWarnEl.textContent =
					`Current PPSF ($${this.pricePerSqft.toLocaleString()}) is below ${anchorLabel} — slider can only adjust upward.`;
				this.sliderWarnEl.style.display = '';
			} else {
				this.sliderWarnEl.style.display = 'none';
			}
		}
	}

	private renderCompositionRows(state: IDealState, m: ReturnType<typeof computeDealMetrics>): void {
		if (!this.compositionRowsEl || !this.compositionTotalEl) {
			return;
		}
		while (this.compositionRowsEl.firstChild) {
			this.compositionRowsEl.removeChild(this.compositionRowsEl.firstChild);
		}
		this.compositionRowsEl.appendChild(this.compRow('Base ARV', formatCurrency(state.baseARV || 0)));
		if (m.enclosedAddedValue > 0) {
			this.compositionRowsEl.appendChild(this.compRow(
				`+ Enclosed sqft value (${state.enclosedSqft || 0} × $${state.enclosedValuePerSqft || 0}/sqft)`,
				formatCurrency(m.enclosedAddedValue),
			));
		}
		const mm = state.marketingMultiplier || 0;
		if (mm > 0) {
			this.compositionRowsEl.appendChild(this.compRow(
				`+ ${mm.toFixed(1)}% marketing leverage`,
				formatCurrency(m.adjustedARV - m.arvWithEnclosed),
			));
		}
		this.compositionTotalEl.textContent = formatCurrency(m.adjustedARV);
	}

	private renderSoundness(m: ReturnType<typeof computeDealMetrics>): void {
		if (!this.soundnessListEl || !this.soundnessNoteEl) {
			return;
		}
		while (this.soundnessListEl.firstChild) {
			this.soundnessListEl.removeChild(this.soundnessListEl.firstChild);
		}
		this.soundnessListEl.appendChild(this.soundnessRow('Profit margin', `${m.profitMarginPct.toFixed(1)}%`, 'Target ≥15% (≥20% strong)', gradeProfitMargin(m.profitMarginPct).color));
		if (!m.isCashDeal) {
			this.soundnessListEl.appendChild(this.soundnessRow('LTC (loan-to-cost)', `${m.ltcPct.toFixed(1)}%`, 'Target ≤80% (≤75% strong)', gradeLTC(m.ltcPct).color));
			this.soundnessListEl.appendChild(this.soundnessRow('LTV (loan-to-value)', `${m.ltvPct.toFixed(1)}%`, 'Target ≤75% (≤70% strong)', gradeLTV(m.ltvPct).color));
		}
		this.soundnessNoteEl.style.display = m.isCashDeal ? '' : 'none';
	}

	private setInputValue(input: HTMLInputElement | null, value: number): void {
		if (!input || document.activeElement === input) {
			return;
		}
		input.value = value > 0 ? Number(value).toLocaleString('en-US') : '';
	}

	// ─── Scaffold ───

	private scaffoldShell(): void {
		if (!this.rootEl) {
			return;
		}
		const initialMode = this.host.getState().mode || 'reno';
		const handles = panelSection({
			rootEl: this.rootEl,
			icon: 'trending_up',
			title: initialMode === 'build' ? 'As Completed Value (ACV)' : 'Post-Renovation Value (ARV)',
		});
		this.titleEl = handles.titleEl;
		this.subtitleEl = handles.subtitleEl;
		this.bodyEl = handles.bodyEl;
	}

	private scaffoldBody(): void {
		if (!this.bodyEl) {
			return;
		}
		this.bodyEl.appendChild(this.scaffoldControlRow());
		this.bodyEl.appendChild(this.scaffoldSnapRow());
		this.bodyEl.appendChild(this.scaffoldSqftHint());
		this.bodyEl.appendChild(this.scaffoldSliderBlock());
		this.bodyEl.appendChild(this.scaffoldCompositionBlock());
		this.bodyEl.appendChild(this.scaffoldSeventyRuleBlock());
		this.bodyEl.appendChild(this.scaffoldSoundnessBlock());
	}

	private scaffoldControlRow(): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-arv__controls';
		this.controlRowEl = row;

		// Base ARV / ACV input
		const baseField = document.createElement('div');
		baseField.className = 'lb-deal-field lb-deal-arv__base';
		const baseLabel = document.createElement('label');
		baseLabel.className = 'lb-deal-field__label';
		this.baseLabelEl = baseLabel;
		baseField.appendChild(baseLabel);
		const baseWrap = currencyInput({
			value: 0,
			size: 'large',
			focusKey: 'arv-base',
			onChange: v => {
				this.host.update({baseARV: Math.max(0, v)});
				this.recomputePPSFFromState();
				this.render();
			},
		});
		this.baseInput = baseWrap.querySelector('input');
		baseField.appendChild(baseWrap);
		row.appendChild(baseField);

		// Price / sqft input
		const ppsfField = document.createElement('div');
		ppsfField.className = 'lb-deal-field lb-deal-arv__ppsf';
		const ppsfLabel = document.createElement('label');
		ppsfLabel.className = 'lb-deal-field__label';
		const ppsfLabelText = document.createElement('span');
		this.ppsfLabelText = ppsfLabelText;
		ppsfLabel.appendChild(ppsfLabelText);
		const linkedBadge = document.createElement('span');
		linkedBadge.className = 'lb-deal-badge lb-deal-badge--linked';
		linkedBadge.textContent = 'linked';
		linkedBadge.style.display = 'none';
		this.ppsfLinkedBadge = linkedBadge;
		ppsfLabel.appendChild(linkedBadge);
		ppsfField.appendChild(ppsfLabel);
		const ppsfWrap = currencyInput({
			value: 0,
			size: 'large',
			focusKey: 'arv-ppsf',
			onChange: v => this.handlePricePerSqftChange(Math.max(0, v)),
		});
		this.ppsfInput = ppsfWrap.querySelector('input');
		ppsfField.appendChild(ppsfWrap);
		row.appendChild(ppsfField);

		// Linked toggle (reno only)
		const linkBtn = document.createElement('button');
		linkBtn.type = 'button';
		linkBtn.className = 'lb-deal-arv__link-btn';
		this.linkBtn = linkBtn;
		const linkIcon = document.createElement('span');
		linkIcon.className = 'material-icons';
		linkIcon.setAttribute('aria-hidden', 'true');
		this.linkIcon = linkIcon;
		linkBtn.appendChild(linkIcon);
		linkBtn.addEventListener('click', () => this.toggleValueSync());
		row.appendChild(linkBtn);

		return row;
	}

	// "Snap to →" mini-action row (2026-06-09 redesign). The two
	// snap buttons used to live inside the control row alongside
	// the base/ppsf inputs, each rendered as a full-width pill
	// reading "Snap to 20% ROI target" / "Snap to Ideal Max". The
	// shared "Snap to" prefix was repeated twice and stretched the
	// pills wider than necessary; move it out as a single label and
	// let the buttons size to their content.
	private scaffoldSnapRow(): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-arv__snap-row';

		const label = document.createElement('span');
		label.className = 'lb-deal-arv__snap-label';
		label.textContent = 'Snap to';
		row.appendChild(label);

		const arrow = document.createElement('span');
		arrow.className = 'lb-deal-arv__snap-arrow material-icons';
		arrow.setAttribute('aria-hidden', 'true');
		arrow.textContent = 'arrow_forward';
		row.appendChild(arrow);

		// Snap to 20% ROI. The earlier scaffold passed `crisis_alert`
		// to `appendIconButtonContent` for a warning-glyph prefix —
		// but `crisis_alert` lives in Material Symbols, not the
		// Material Icons font that's actually loaded on this surface,
		// so the span fell back to literal text ("crisis_alert" ≈
		// 168px wide). It used to be hidden because the button was
		// full-width and clipped; after #87 sized the button to its
		// content, the bogus text ballooned the pill. Drop the icon
		// — the colored pill already signals the CTAs.
		const snap20 = document.createElement('button');
		snap20.type = 'button';
		snap20.className = 'lb-deal-arv__snap lb-deal-arv__snap--roi';
		snap20.textContent = '20% ROI target';
		snap20.addEventListener('click', () => this.snapTo20ROI());
		this.snap20Btn = snap20;
		row.appendChild(snap20);

		// Snap to Ideal Max
		const snapIM = document.createElement('button');
		snapIM.type = 'button';
		snapIM.className = 'lb-deal-arv__snap lb-deal-arv__snap--imo';
		snapIM.textContent = 'Ideal Max';
		snapIM.addEventListener('click', () => this.snapToIdealMax());
		this.snapIMBtn = snapIM;
		row.appendChild(snapIM);

		return row;
	}

	private scaffoldSqftHint(): HTMLElement {
		const hint = document.createElement('div');
		hint.className = 'lb-deal-arv__sqft-hint';
		hint.textContent = 'Enter property sqft (in Acquisition section) to enable price-per-sqft.';
		hint.style.display = 'none';
		this.sqftHintEl = hint;
		return hint;
	}

	private scaffoldSliderBlock(): HTMLElement {
		const block = document.createElement('div');
		block.className = 'lb-deal-arv__slider';
		this.sliderBlockEl = block;

		const head = document.createElement('div');
		head.className = 'lb-deal-arv__slider-head';
		const headInfo = document.createElement('div');
		const title = document.createElement('div');
		title.className = 'lb-deal-arv__slider-title';
		title.textContent = 'Adjust price / sqft';
		headInfo.appendChild(title);
		const subtitle = document.createElement('div');
		subtitle.className = 'lb-deal-arv__slider-hint';
		this.sliderSubtitleEl = subtitle;
		headInfo.appendChild(subtitle);
		head.appendChild(headInfo);
		const readout = document.createElement('div');
		readout.className = 'lb-deal-arv__slider-readout';
		this.sliderReadoutEl = readout;
		head.appendChild(readout);
		block.appendChild(head);

		const slider = document.createElement('input');
		slider.type = 'range';
		slider.step = '1';
		slider.className = 'lb-deal-arv__slider-input';
		this.sliderEl = slider;
		slider.addEventListener('input', () => {
			const v = parseInt(slider.value, 10) || 0;
			// Update the readout in-place so the live "$X/sqft" feedback
			// stays in sync without waiting for the full render to land.
			if (this.sliderReadoutEl) {
				this.sliderReadoutEl.textContent = `$${v.toLocaleString()}/sqft`;
			}
			this.handlePricePerSqftChange(v);
		});
		block.appendChild(slider);

		const ticks = document.createElement('div');
		ticks.className = 'lb-deal-arv__slider-ticks';
		this.sliderTickEls = [];
		for (let i = 0; i < 3; i++) {
			const tick = document.createElement('span');
			ticks.appendChild(tick);
			this.sliderTickEls.push(tick);
		}
		block.appendChild(ticks);

		const warn = document.createElement('div');
		warn.className = 'lb-deal-arv__slider-warn';
		warn.style.display = 'none';
		this.sliderWarnEl = warn;
		block.appendChild(warn);

		return block;
	}

	private scaffoldCompositionBlock(): HTMLElement {
		const block = document.createElement('div');
		block.className = 'lb-deal-arv__composition';
		this.compositionBlockEl = block;

		const head = document.createElement('div');
		head.className = 'lb-deal-arv__composition-title';
		head.textContent = 'ARV composition';
		block.appendChild(head);

		const rowsEl = document.createElement('div');
		this.compositionRowsEl = rowsEl;
		block.appendChild(rowsEl);

		const total = document.createElement('div');
		total.className = 'lb-deal-arv__composition-total';
		const tLabel = document.createElement('span');
		tLabel.textContent = 'Final ARV';
		total.appendChild(tLabel);
		const tVal = document.createElement('span');
		this.compositionTotalEl = tVal;
		total.appendChild(tVal);
		block.appendChild(total);

		return block;
	}

	private compRow(label: string, value: string): HTMLElement {
		const row = document.createElement('div');
		row.className = 'lb-deal-arv__composition-row';
		const lbl = document.createElement('span');
		lbl.textContent = label;
		row.appendChild(lbl);
		const val = document.createElement('span');
		val.textContent = value;
		row.appendChild(val);
		return row;
	}

	private scaffoldSeventyRuleBlock(): HTMLElement {
		const block = document.createElement('div');
		block.className = 'lb-deal-rule70';
		this.seventyRuleBlockEl = block;
		const head = document.createElement('div');
		head.className = 'lb-deal-rule70__head';
		const title = document.createElement('span');
		title.className = 'lb-deal-rule70__title';
		title.textContent = '70% Rule Check';
		head.appendChild(title);
		const status = document.createElement('span');
		status.className = 'lb-deal-rule70__status';
		this.seventyRuleStatusEl = status;
		head.appendChild(status);
		block.appendChild(head);
		const detail = document.createElement('div');
		detail.className = 'lb-deal-rule70__detail';
		this.seventyRuleDetailEl = detail;
		block.appendChild(detail);
		return block;
	}

	private scaffoldSoundnessBlock(): HTMLElement {
		const block = document.createElement('div');
		block.className = 'lb-deal-soundness';
		this.soundnessBlockEl = block;
		const title = document.createElement('div');
		title.className = 'lb-deal-soundness__title';
		title.textContent = 'Build Soundness Check';
		block.appendChild(title);
		const list = document.createElement('div');
		list.className = 'lb-deal-soundness__list';
		this.soundnessListEl = list;
		block.appendChild(list);
		const note = document.createElement('div');
		note.className = 'lb-deal-soundness__note';
		note.textContent = 'Cash deal — LTC and LTV not applicable. Add a financed amount in Holding Costs to track leverage.';
		note.style.display = 'none';
		this.soundnessNoteEl = note;
		block.appendChild(note);
		return block;
	}

	private soundnessRow(label: string, value: string, hint: string, color: string): HTMLElement {
		const row = document.createElement('div');
		row.className = `lb-deal-soundness__row lb-deal-soundness__row--${color}`;
		const left = document.createElement('div');
		const lbl = document.createElement('div');
		lbl.className = 'lb-deal-soundness__row-label';
		lbl.textContent = label;
		left.appendChild(lbl);
		const h = document.createElement('div');
		h.className = 'lb-deal-soundness__row-hint';
		h.textContent = hint;
		left.appendChild(h);
		row.appendChild(left);
		const val = document.createElement('div');
		val.className = 'lb-deal-soundness__row-value';
		val.textContent = value;
		row.appendChild(val);
		return row;
	}

	// ─── Handlers ───

	// PPSF write-path: edit base ARV and (when linked) the enclosed
	// value/sqft so the leverage panel and ARV stay in lockstep.
	private handlePricePerSqftChange(newPPSF: number): void {
		this.pricePerSqft = newPPSF;
		const state = this.host.getState();
		const sqft = state.property.sqft || 0;
		const patch: Partial<IDealState> = {};
		if (sqft > 0) {
			patch.baseARV = Math.round(newPPSF * sqft);
		}
		if (state.valueSyncedToPPSF) {
			patch.enclosedValuePerSqft = newPPSF;
		}
		if (Object.keys(patch).length > 0) {
			this.host.update(patch);
		}
		this.render();
	}

	// Mirrors src/components/DealEditor.jsx::snapTo20ROI — the React
	// reference subtracts enclosedAddedValue but does NOT divide by the
	// marketing-multiplier factor; we replicate that exactly so the
	// buttons land identical numbers across both implementations.
	private snapTo20ROI(): void {
		const m = computeDealMetrics(this.host.getState());
		const prefill = Math.max(0, m.targetARV20 - m.enclosedAddedValue);
		this.host.update({baseARV: prefill});
		this.recomputePPSFFromState();
		this.render();
	}

	private snapToIdealMax(): void {
		const state = this.host.getState();
		const m = computeDealMetrics(state);
		const mmFactor = Math.max(0.01, 1 + (state.marketingMultiplier || 0) / 100);
		const arvWithEnclosedTarget = m.targetARVForIMO / mmFactor;
		const baseARVTarget = Math.max(0, arvWithEnclosedTarget - m.enclosedAddedValue);
		this.host.update({baseARV: Math.round(baseARVTarget)});
		this.recomputePPSFFromState();
		this.render();
	}

	private toggleValueSync(): void {
		const state = this.host.getState();
		const next = !state.valueSyncedToPPSF;
		const patch: Partial<IDealState> = {valueSyncedToPPSF: next};
		if (next && this.pricePerSqft > 0) {
			patch.enclosedValuePerSqft = this.pricePerSqft;
		}
		this.host.update(patch);
		this.render();
	}

	private recomputePPSFFromState(): void {
		const state = this.host.getState();
		const sqft = state.property.sqft || 0;
		if (sqft > 0) {
			this.pricePerSqft = Math.round((state.baseARV || 0) / sqft);
		}
	}

	private appendIconButtonContent(btn: HTMLButtonElement, icon: string, label: string): void {
		btn.textContent = '';
		const iconEl = document.createElement('span');
		iconEl.className = 'material-icons';
		iconEl.setAttribute('aria-hidden', 'true');
		iconEl.textContent = icon;
		btn.appendChild(iconEl);
		const labelEl = document.createElement('span');
		labelEl.textContent = label;
		btn.appendChild(labelEl);
	}
}

function formatCurrency(n: number): string {
	if (!Number.isFinite(n)) {
		return '$0';
	}
	return n.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
}
